import type React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getAdminUrl } from '@/utils/crossAppLinks';
import { MediaFrame, type Frame, type Media } from './Media';

/* ==========================================================================
   The building blocks the marketing pages share. The copy rule they encode
   (docs/plans/MARKETING_SITE.md): headings are the questions a booker would
   ask, in sentence case, answered with how the thing works. There is no
   kicker stamp and no label/value list under each answer, which is the
   template the old pages repeated four times each.
   ========================================================================== */

/** The underlined text link used beside buttons. Internal paths route, full
    URLs (the admin app) navigate. */
export const TextLink: React.FC<{ to: string; children: React.ReactNode; className?: string }> = ({
  to,
  children,
  className,
}) => {
  const classes = cn(
    'text-sm font-medium text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors',
    className,
  );
  return to.startsWith('/') && !to.startsWith('//') ? (
    <Link to={to} className={classes}>{children}</Link>
  ) : (
    <a href={to} className={classes}>{children}</a>
  );
};

/** The one primary action on the site. Operators are curated while
    Showliner is early (ADR-0018), so it asks for access rather than
    promising an account. */
export const RequestAccessButton: React.FC<{ size?: 'default' | 'lg' }> = ({ size = 'lg' }) => (
  <Button size={size} asChild>
    <a href={getAdminUrl('/register')}>Request access</a>
  </Button>
);

/** Top of a page: one plain H1 that says what the page is about, a short
    paragraph, and the actions. */
export const PageHero: React.FC<{
  title: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, children, actions }) => (
  <section>
    <div className="mx-auto max-w-6xl px-4 sm:px-8 page-top">
      <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.25rem)] leading-[1] tracking-tight text-ink max-w-[20ch] text-balance">
        {title}
      </h1>
      {children && (
        <div className="mt-6 max-w-[56ch] space-y-4 text-lg text-grey-800 leading-relaxed">{children}</div>
      )}
      {actions && <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">{actions}</div>}
    </div>
  </section>
);

/** Body copy inside an answer. */
export const Answer: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('space-y-4 text-base text-grey-800 leading-relaxed max-w-[60ch]', className)}>{children}</div>
);

/**
 * A question and its answer, with the screen it is about beside it. Media
 * alternates sides down a page (`flip`) so a long page doesn't read as one
 * column of text next to one column of pictures.
 */
export const QuestionSection: React.FC<{
  id?: string;
  question: string;
  children: React.ReactNode;
  media?: Media;
  frame?: Frame;
  caption?: string;
  flip?: boolean;
}> = ({ id, question, children, media, frame, caption, flip }) => {
  const isPhone = frame === 'phone';
  return (
    <article id={id} className="scroll-mt-20 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-8 items-center">
      <div className={cn(media ? (isPhone ? 'lg:col-span-7' : 'lg:col-span-5') : 'lg:col-span-8', media && flip && 'lg:order-2')}>
        <h2 className="font-display text-3xl sm:text-[2.5rem] text-ink leading-[1.05] tracking-tight text-balance">
          {question}
        </h2>
        <Answer className="mt-5">{children}</Answer>
      </div>
      {media && (
        <div className={cn(isPhone ? 'lg:col-span-5' : 'lg:col-span-7', flip && 'lg:order-1')}>
          <MediaFrame media={media} frame={frame} caption={caption} />
        </div>
      )}
    </article>
  );
};

/** A run of QuestionSections with the page's section rhythm. */
export const QuestionList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="border-t border-grey-200">
    <div className="mx-auto max-w-6xl px-4 sm:px-8 section space-y-20 sm:space-y-28">{children}</div>
  </section>
);

/** The last band on a page: a plain heading, a sentence or two, the action. */
export const ClosingBand: React.FC<{
  title: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, children, actions }) => (
  <section className="border-t border-grey-200 bg-grey-100/60">
    <div className="mx-auto max-w-6xl px-4 sm:px-8 section">
      <h2 className="font-display text-[clamp(1.75rem,3.5vw,2.75rem)] text-ink leading-[1.05] tracking-tight max-w-[24ch] text-balance">
        {title}
      </h2>
      {children && <Answer className="mt-5 text-lg">{children}</Answer>}
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
        {actions ?? <RequestAccessButton />}
      </div>
    </div>
  </section>
);
